// useHotkeys(bindings, { enabled? })     (DESIGN_SPEC §14 keyboard map)
// Bindings map a key to a handler: "?" "/" "n" "j" "k" "t" "f" "c" "1"…"9" "Enter" "Escape" "End"
// "Shift+C" "Ctrl+Enter" (Ctrl also matches Cmd) and two-key sequences "g a" (second key
// within 1.5 s). One window listener serves every registration; the most recently mounted
// registration wins, and a handler that returns false passes the key on. While typing in a
// field only Escape and modified keys (Ctrl+Enter) are delivered. A handled key is
// preventDefault-ed. Handlers always see the latest closure (no re-registration per render).
//   formatKey("Ctrl+Enter") → [["Ctrl", "Enter"]] · formatKey("g a") → [["G"], ["A"]]   (one <kbd> per key)
import { useEffect, useLayoutEffect, useRef } from "react";

type HotkeyHandler = (e: KeyboardEvent) => void | boolean;
type HotkeyBindings = Record<string, HotkeyHandler>;
type HotkeyOptions = { enabled?: boolean };

type Registration = { bindings: () => HotkeyBindings; enabled: () => boolean };

const SEQUENCE_MS = 1500;
const registrations: Registration[] = [];
let pendingPrefix: { key: string; at: number } | null = null;
const canonicalCache = new Map<string, string>();

const ALIASES: Record<string, string> = { esc: "Escape", escape: "Escape", enter: "Enter", return: "Enter", end: "End", home: "Home", space: " ", up: "ArrowUp", down: "ArrowDown", left: "ArrowLeft", right: "ArrowRight", tab: "Tab" };

/** "Shift+C" → "Shift+c", "ctrl+enter" → "Ctrl+Enter", "Mod+Enter" → "Ctrl+Enter", "Esc" → "Escape". */
function canonicalChord(spec: string): string {
  const parts = spec.split("+");
  // "+" itself as the key ("Shift++") leaves an empty last part
  const keyRaw = parts.length > 1 && parts[parts.length - 1] === "" ? "+" : parts.pop() ?? "";
  const mods = new Set(parts.filter(Boolean).map((m) => m.toLowerCase()));
  let key = ALIASES[keyRaw.toLowerCase()] ?? keyRaw;
  if (key.length === 1) key = key.toLowerCase();
  const out: string[] = [];
  if (mods.has("ctrl") || mods.has("control") || mods.has("mod") || mods.has("meta") || mods.has("cmd")) out.push("Ctrl");
  if (mods.has("alt") || mods.has("option")) out.push("Alt");
  if (mods.has("shift")) out.push("Shift");
  out.push(key);
  return out.join("+");
}

function canonical(spec: string): string {
  let c = canonicalCache.get(spec);
  if (c === undefined) {
    c = spec.trim().split(/\s+/).map(canonicalChord).join(" ");
    canonicalCache.set(spec, c);
  }
  return c;
}

/** The pressed chord in canonical form. Shift is implied by symbols ("?"), explicit for letters and named keys. */
function chordOf(e: KeyboardEvent): string {
  const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
  const out: string[] = [];
  if (e.ctrlKey || e.metaKey) out.push("Ctrl");
  if (e.altKey) out.push("Alt");
  if (e.shiftKey && (e.key.length > 1 || /[a-z]/i.test(e.key))) out.push("Shift");
  out.push(key);
  return out.join("+");
}

function isTyping(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  if (t.isContentEditable) return true;
  if (t instanceof HTMLTextAreaElement || t instanceof HTMLSelectElement) return true;
  if (t instanceof HTMLInputElement) return !["checkbox", "radio", "button", "submit", "reset", "range", "color", "file"].includes(t.type);
  return false;
}

function find(chord: string, typing: boolean, e: KeyboardEvent): boolean {
  if (typing && chord !== "Escape" && !chord.startsWith("Ctrl+") && !chord.startsWith("Alt+")) return false;
  for (let i = registrations.length - 1; i >= 0; i--) {
    const r = registrations[i];
    if (!r.enabled()) continue;
    for (const [spec, handler] of Object.entries(r.bindings())) {
      if (canonical(spec) !== chord) continue;
      if (handler(e) === false) continue;
      e.preventDefault();
      return true;
    }
  }
  return false;
}

function hasPrefix(chord: string): boolean {
  return registrations.some((r) => r.enabled() && Object.keys(r.bindings()).some((s) => canonical(s).startsWith(`${chord} `)));
}

function onKeyDown(e: KeyboardEvent) {
  if (e.defaultPrevented || e.isComposing || e.key === "Dead" || e.key === "Unidentified") return;
  const typing = isTyping(e.target);
  const chord = chordOf(e);
  const prefix = pendingPrefix;
  pendingPrefix = null;
  if (prefix && !typing && Date.now() - prefix.at < SEQUENCE_MS && find(`${prefix.key} ${chord}`, false, e)) return;
  if (find(chord, typing, e)) return;
  if (!typing && !e.repeat && hasPrefix(chord)) pendingPrefix = { key: chord, at: Date.now() };
}

function register(r: Registration): () => void {
  if (registrations.length === 0) window.addEventListener("keydown", onKeyDown);
  registrations.push(r);
  return () => {
    const i = registrations.indexOf(r);
    if (i >= 0) registrations.splice(i, 1);
    if (registrations.length === 0) {
      window.removeEventListener("keydown", onKeyDown);
      pendingPrefix = null;
    }
  };
}

export function useHotkeys(bindings: HotkeyBindings, opts: HotkeyOptions = {}): void {
  const latest = useRef({ bindings, enabled: opts.enabled ?? true });
  useLayoutEffect(() => {
    latest.current = { bindings, enabled: opts.enabled ?? true };
  });
  useEffect(
    () =>
      register({
        bindings: () => latest.current.bindings,
        enabled: () => latest.current.enabled,
      }),
    [],
  );
}

export function formatKey(spec: string): string[][] {
  const c = canonical(spec);
  // the space bar is itself a " " key, not a sequence separator
  return (c === " " ? [c] : c.split(" "))
    .map((chord) =>
      chord.split("+").map((k) => (k === " " ? "Space" : k.length === 1 ? k.toUpperCase() : k.replace(/^Arrow/, ""))),
    );
}
